/**
 * cycleUtils.ts
 * Global EPR cycle utility — quarter label resolution, date range,
 * and KR relevance filtering.
 *
 * Quarter label format used in OKR Tracker:  "Q1 2026", "Q2 2026", …
 * EPR cycle period types: Q1 | Q2 | Q3 | Q4 | H1 | H2 | annual
 */

import type { CyclePeriod } from '../types';

// ─── Quarter date ranges (standard calendar) ─────────────────────────────────

const QUARTER_MONTHS: Record<string, [number, number]> = {
  Q1: [0, 2],   // Jan–Mar
  Q2: [3, 5],   // Apr–Jun
  Q3: [6, 8],   // Jul–Sep
  Q4: [9, 11],  // Oct–Dec
};

/** Returns the calendar start and end Date for a given EPR cycle period + year. */
export function getCycleDateRange(period: CyclePeriod, year: number): { start: Date; end: Date } {
  switch (period) {
    case 'Q1': return { start: new Date(year, 0, 1), end: new Date(year, 2, 31, 23, 59, 59) };
    case 'Q2': return { start: new Date(year, 3, 1), end: new Date(year, 5, 30, 23, 59, 59) };
    case 'Q3': return { start: new Date(year, 6, 1), end: new Date(year, 8, 30, 23, 59, 59) };
    case 'Q4': return { start: new Date(year, 9, 1), end: new Date(year, 11, 31, 23, 59, 59) };
    case 'H1': return { start: new Date(year, 0, 1), end: new Date(year, 5, 30, 23, 59, 59) };
    case 'H2': return { start: new Date(year, 6, 1), end: new Date(year, 11, 31, 23, 59, 59) };
    case 'annual': return { start: new Date(year, 0, 1), end: new Date(year, 11, 31, 23, 59, 59) };
    default: return { start: new Date(year, 0, 1), end: new Date(year, 11, 31, 23, 59, 59) };
  }
}

/**
 * Returns the OKR Tracker-format quarter label(s) covered by an EPR period.
 * e.g. period="Q2", year=2026 → ["Q2 2026"]
 *      period="H1", year=2026 → ["Q1 2026", "Q2 2026"]
 *      period="annual", year=2026 → ["Q1 2026", "Q2 2026", "Q3 2026", "Q4 2026"]
 */
export function getOKRQuarterLabels(period: CyclePeriod, year: number): string[] {
  switch (period) {
    case 'Q1': return [`Q1 ${year}`];
    case 'Q2': return [`Q2 ${year}`];
    case 'Q3': return [`Q3 ${year}`];
    case 'Q4': return [`Q4 ${year}`];
    case 'H1': return [`Q1 ${year}`, `Q2 ${year}`];
    case 'H2': return [`Q3 ${year}`, `Q4 ${year}`];
    case 'annual': return [`Q1 ${year}`, `Q2 ${year}`, `Q3 ${year}`, `Q4 ${year}`];
    default: return [];
  }
}

/** Human-readable label for the EPR cycle period, e.g. "Q2 2026 · Apr–Jun" */
export function getCycleDisplayLabel(period: CyclePeriod, year: number): string {
  const range = getCycleDateRange(period, year);
  const opts: Intl.DateTimeFormatOptions = { month: 'short' };
  const startLabel = range.start.toLocaleString('en-GB', opts);
  const endLabel   = range.end.toLocaleString('en-GB', opts);

  const periodLabel = period === 'annual' ? `Annual ${year}` : `${period} ${year}`;
  const dateRange   = period === 'annual'
    ? `Jan–Dec ${year}`
    : `${startLabel}–${endLabel} ${year}`;

  return `${periodLabel} · ${dateRange}`;
}

/** Short badge label, e.g. "Q2 2026" or "H1 2026" */
export function getCycleBadgeLabel(period: CyclePeriod, year: number): string {
  return period === 'annual' ? `Annual ${year}` : `${period} ${year}`;
}

// ─── Date range → Quarter labels ─────────────────────────────────────────────

/**
 * Derives OKR Tracker quarter labels from an actual date range.
 * Scans month-by-month from `start` to `end` and collects unique labels.
 * e.g. start=Mar 2026, end=Apr 2026 → ["Q1 2026", "Q2 2026"]
 *      start=Jan 2026, end=Jun 2026 → ["Q1 2026", "Q2 2026"]
 */
export function getQuartersFromDateRange(start: Date, end: Date): string[] {
  const quarters: string[] = [];
  const current = new Date(start.getFullYear(), start.getMonth(), 1);
  const endMonth = new Date(end.getFullYear(), end.getMonth(), 1);
  while (current <= endMonth) {
    const month = current.getMonth();
    const year = current.getFullYear();
    let q: string;
    if (month <= 2)      q = `Q1 ${year}`;
    else if (month <= 5) q = `Q2 ${year}`;
    else if (month <= 8) q = `Q3 ${year}`;
    else                 q = `Q4 ${year}`;
    if (!quarters.includes(q)) quarters.push(q);
    current.setMonth(current.getMonth() + 1);
  }
  return quarters;
}

/** Returns true if the review cycle has passed its lock date (read-only after this). */
export function isCycleLocked(lockDate: Date): boolean {
  return new Date() >= lockDate;
}

// ─── KR Relevance filtering ───────────────────────────────────────────────────

export interface CycleContext {
  period: CyclePeriod;
  year: number;
  /** Fallback: EPR cycle's own startDate as JS Date */
  startDate?: Date;
  /** Fallback: EPR cycle's own endDate as JS Date */
  endDate?: Date;
}

export interface CycleAwareKR {
  krId: string;
  krTitle: string;
  krNumber?: string;
  selfScore: number;
  evidence: string;
  progress?: number;
  krStatus?: string;
  outcome?: string;
  sourceOfVerification?: string;
  /** Which OKR quarters this KR belongs to (from keyResults.quarters or .quarter) */
  krQuarters?: string[];
  /** Quarter-specific progress snapshot */
  quarterProgress?: number;
  /** Whether this KR is directly relevant to the EPR cycle period */
  isRelevantToCycle: boolean;
}

/**
 * Filters and annotates KRs for relevance to an EPR cycle.
 * A KR is "relevant" if:
 *  1. Its `quarters` array (or legacy `quarter` string) overlaps the EPR period's quarter labels, OR
 *  2. No quarter info → fall back to date overlap using EPR cycle start/end, OR
 *  3. No date info → include (show all, soft-filter)
 */
export function filterKRsForCycle<T extends {
  krId: string;
  progress?: number;
  krStatus?: string;
  quarter?: string;
  quarters?: string[];
  quarterlyBreakdown?: Record<string, { progress?: number }>;
}>(krs: T[], ctx: CycleContext): (T & { isRelevantToCycle: boolean; quarterProgress?: number; krQuarters?: string[] })[] {
  const targetLabels = new Set(getOKRQuarterLabels(ctx.period, ctx.year));

  return krs.map(kr => {
    // Collect what quarters this KR covers
    const krQuarters: string[] = kr.quarters
      ? kr.quarters
      : kr.quarter
      ? [kr.quarter]
      : [];

    // Check overlap
    const hasQuarterInfo = krQuarters.length > 0;
    const overlaps = hasQuarterInfo
      ? krQuarters.some(q => targetLabels.has(q))
      : true; // no quarter info → include by default

    // Try to get quarter-specific progress
    let quarterProgress: number | undefined;
    if (kr.quarterlyBreakdown) {
      for (const label of targetLabels) {
        const bd = kr.quarterlyBreakdown[label];
        if (bd?.progress !== undefined) {
          quarterProgress = bd.progress;
          break;
        }
      }
    }
    // Fall back to overall progress
    if (quarterProgress === undefined) quarterProgress = kr.progress;

    return {
      ...kr,
      krQuarters,
      quarterProgress,
      isRelevantToCycle: overlaps,
    };
  });
}

/**
 * Returns only the KRs relevant to the EPR cycle, with quarter-specific progress.
 * KRs without any quarter info are included (safe default).
 */
export function getRelevantKRs<T extends {
  krId: string;
  progress?: number;
  krStatus?: string;
  quarter?: string;
  quarters?: string[];
  quarterlyBreakdown?: Record<string, { progress?: number }>;
}>(krs: T[], ctx: CycleContext): (T & { isRelevantToCycle: boolean; quarterProgress?: number; krQuarters?: string[] })[] {
  return filterKRsForCycle(krs, ctx).filter(kr => kr.isRelevantToCycle);
}

/** Detect which quarter today falls in */
export function detectCurrentQuarter(): { quarter: string; year: number } {
  const now = new Date();
  const month = now.getMonth();
  const year = now.getFullYear();
  const q = Object.entries(QUARTER_MONTHS).find(([, [s, e]]) => month >= s && month <= e);
  return { quarter: q?.[0] ?? 'Q1', year };
}
